import { Button, MenuItem, Intent } from '@blueprintjs/core';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React from 'react';
import { Page, TLCode } from '../../common/enums';
import {
  PharmacyDetailedStore,
  PharmacyStore,
  SecretariatStore,
  SecretariatNeighborStore,
  GroupStore,
  AnchorStore,
  PharmacyStatusStore,
  AuthenticationStore,
  ApplicationStore,
} from '../../store';
import { Pharmacy, Secretariat } from '../../store/models';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { IItemRendererProps, MultiSelect } from '@blueprintjs/select';
import { IconNames } from '@blueprintjs/icons';
import {
  DataGrid,
  Column,
  Button as DGButton,
  Sorting,
  SearchPanel,
  DataGridTypes,
  Export,
  FilterRow,
  FilterPanel,
  HeaderFilter,
  GroupPanel,
  Grouping,
  ColumnChooser,
  Pager,
  Paging,
} from 'devextreme-react/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { PharmacyStatusCell } from '../common/grid/PharmacyStatusCell';

interface SecretariatNeighborsTabProps extends WithTranslateProps {
  readOnly: boolean;
  secretariat?: Instance<typeof Secretariat>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  SecretariatNeighborStore?: Instance<typeof SecretariatNeighborStore>;
  GroupStore?: Instance<typeof GroupStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  PharmacyStatusStore?: Instance<typeof PharmacyStatusStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  refreshSecretariatPharmacyGeodata: (secretariatId: number) => void;
}

interface SecretariatNeighborsTabState {
  isLoading: boolean;
  pharmaciesToLink: Array<Instance<typeof Pharmacy>>;
}

@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@inject('SecretariatStore')
@inject('SecretariatNeighborStore')
@inject('GroupStore')
@inject('AnchorStore')
@inject('PharmacyStatusStore')
@inject('AuthenticationStore')
@inject('ApplicationStore')
@observer
class SecretariatNeighborsTab extends React.Component<SecretariatNeighborsTabProps, SecretariatNeighborsTabState> {
  state = {
    isLoading: false,
    pharmaciesToLink: [],
  } as SecretariatNeighborsTabState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    await this.props.GroupStore!.checkListLoaded();
    await this.props.AnchorStore!.checkListLoaded();
    await this.props.PharmacyStore!.checkListLoaded();
    await this.props.SecretariatStore!.checkListLoaded();
    await this.props.SecretariatNeighborStore!.checkListLoaded();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private pharmaciesWithoutSecretariat = () => {
    return this.props
      .PharmacyStore!.dataList.filter((elem) => {
        return elem.secretariatId === undefined;
      })
      .filter((elem) => this.state.pharmaciesToLink.findIndex((p) => p.pharmacyId === elem.pharmacyId) === -1);
  };

  private pharmaciesItemRenderer = (
    item: Instance<typeof Pharmacy>,
    { handleClick, modifiers }: IItemRendererProps,
  ) => {
    if (!modifiers.matchesPredicate) return null;
    return (
      <MenuItem
        active={modifiers.active}
        key={item.pharmacyId}
        onClick={handleClick}
        text={item.apbNumber.toString()}
      />
    );
  };

  private buildGridData = () => {
    return this.props
      .SecretariatNeighborStore!.dataList.filter((elem) => {
        return this.props.secretariat !== undefined && elem.secretariatId === this.props.secretariat!.secretariatId;
            })
            .map((sn) => {
        if (sn.pharmacySimple === null || sn.pharmacySimple === undefined) {
          return ;
        }
        return {
          pharmacyId: sn.pharmacyId,
          apbNumber: sn.pharmacySimple.apbNumber,
          name: sn.pharmacySimple.name,
          level: sn.pharmacySimple.level,
          address: sn.pharmacySimple.address,
          zipCode: sn.pharmacySimple.zipCode,
          city: sn.pharmacySimple.city,
          secretariat: sn.pharmacySimple.secretariatId === undefined
            ? ''
            : this.props.SecretariatStore!.dataList.find((g) => g.secretariatId === sn.pharmacySimple!.secretariatId)!.code,
          group: sn.pharmacySimple.groupId === undefined
            ? ''
            : this.props.GroupStore!.dataList.find((g) => g.groupId === sn.pharmacySimple!.groupId)!.name,
        //anchor: sn.pharmacySimple!.anchorId
        anchor:
          sn.pharmacySimple!.anchorId === undefined
            ? ''
            : this.props.AnchorStore!.dataList.find((a) => a.anchorId === sn.pharmacySimple!.anchorId!)!.name,
      };
    });
  };

  private refreshNeighborhood = async () => {
    await this.props.SecretariatStore!.refreshSecretariatNeighborhood(this.props.secretariat!.secretariatId);
    this.props.SecretariatNeighborStore!.loadList();
  };

  displayPharmacyStatusCell = (params: any) => {
    const pharmacyStatus = this.props.PharmacyStatusStore!.dataList.find(
      (ps) => ps.pharmacyStatusId === params.row.data.statusId,
    );
    if (pharmacyStatus === undefined) return <></>;
    return <PharmacyStatusCell pharmacyStatus={pharmacyStatus} />;
  };

  onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          'Pharmacies_of_secretariat_' + this.props.secretariat!.name + '.xlsx',
        );
      });
    });
  };

  onRowClicked = (ev) => {
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${ev.data.pharmacyId}`);
  };

  render() {
    const { translate } = this.props;
    return (
      <Container>
        <DivLine>
          {this.props.AuthenticationStore!.isSuperAdmin && (
            <DivRightColumn>
              <Button
                text={translate(TLCode.SECRETARIAT_RefreshNeighborhood)}
                icon={IconNames.REFRESH}
                intent={Intent.DANGER}
                onClick={this.refreshNeighborhood}
              />
            </DivRightColumn>
          )}
        </DivLine>
        <br />
        <br />
        <DataGrid
          id="pharmaciesList"
          dataSource={this.buildGridData()}
          showBorders
          onExporting={this.onExporting}
          onRowClick={this.onRowClicked}
          allowColumnResizing
          rowAlternationEnabled={true}
        >
          <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
          <Sorting mode="multiple" />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <FilterRow visible={true} applyFilter={'auto'} />
          <GroupPanel visible={true} />
          <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
          <Grouping autoExpandAll={false} />
          <ColumnChooser enabled={true} />
          <Column dataType="number" dataField="level" caption="" width={40} />
          <Column
            dataType="number"
            dataField="apbNumber"
            caption={translate(TLCode.PHARMACIES_ApbNum)}
            width={80}
            defaultSortOrder="asc"
            sortIndex={1}
          />
          <Column dataType="string" dataField="name" caption={translate(TLCode.PHARMACIES_PharmacyName)} width={180} />
          <Column dataType="string" dataField="address" caption={translate(TLCode.PHARMACIES_Address)} width={200} />
          <Column dataType="string" dataField="zipCode" caption={translate(TLCode.PHARMACIES_ZipCode)} width={55} />
          <Column dataType="string" dataField="city" caption={translate(TLCode.PHARMACIES_City)} width={180} />
          <Column dataType="string" dataField="secretariat" caption="" width={180} />
          <Column
            dataType="string"
            dataField="group"
            caption={translate(TLCode.PHARMACIES_Group)}
            width={80}
            defaultSortOrder="asc"
            sortIndex={0}
          />
          <Column dataType="string" dataField="anchor" caption={translate(TLCode.PHARMACIES_Anchor)} width={60} />
          <Pager
            visible={true}
            allowedPageSizes={[5, 10, 20, 50, 100]}
            showPageSizeSelector={true}
            showInfo={true}
            showNavigationButtons={true}
          />
          <Paging defaultPageSize={20} />
        </DataGrid>
      </Container>
    );
  }
}

export default withTranslate(SecretariatNeighborsTab);

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 20px;
  align-content: center;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const DivRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
`;
